import { PickAssetRow, PickRow, PickSplitRow, TeamRow } from "@/swagger";
import { teams } from "@/shared/utils/teams";
import {
  Text,
  Flex,
  Grid,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@suns/design-system";
import { CornerDownRight, Redo, ShieldHalf } from "lucide-react";
import { PickOutcomeRow } from "@suns/api/generated-client/apollo";
import { formatRank } from "@/shared/utils/helper-functions";

export function PickAssetChart({ pickAsset }: { pickAsset: PickAssetRow }) {
  return (
    <Flex direction="down" gap="lg">
      {pickAsset.picks.map((pick, i) => (
        <Pick key={i} pick={pick} />
      ))}
    </Flex>
  );
}

export function Pick({ pick }: { pick: PickRow }) {
  const columnCount = 30;
  const splitsCount = pick.pickSplits.length;

  return (
    <Grid
      className="mt-4"
      style={{ gridTemplateColumns: `repeat(${columnCount}, 1fr)` }}
    >
      {[...Array(columnCount)].map((_, i) => {
        const pickNumber = i + (pick.round - 1) * 30 + 1;
        const pickOutcomes = pick.pickSplits
          .filter(
            (pickSplit) =>
              pickNumber >= pickSplit.minPickNumber &&
              pickNumber <= pickSplit.maxPickNumber
          )
          .flatMap((pickSplit) => pickSplit.pickOutcomes);

        return (
          <Flex
            direction="right"
            key={i}
            style={{ gridRow: 1, gridColumn: i + 1 }}
          >
            <PickProbabilityItem
              pickNumber={pickNumber}
              pickOutcomes={pickOutcomes}
            />
            {i != columnCount - 1 && <PickTick />}
          </Flex>
        );
      })}

      {pick.pickSplits.map((pickSplit, i) => {
        const numColumns =
          pickSplit.maxPickNumber - pickSplit.minPickNumber + 1;

        return (
          <div
            key={i}
            style={{
              gridRow: 2,
              gridColumn: `span ${numColumns}`,
              position: "relative",
            }}
          >
            <PickSplit pickSplit={pickSplit} />
            {i !== splitsCount - 1 && (
              <div
                className="absolute right-[-0.75rem] top-1/2 z-10 -translate-y-1/2 transform rounded-md
                  bg-white"
              >
                <ShieldHalf color="#4B5563" />
              </div>
            )}
          </div>
        );
      })}
    </Grid>
  );
}

export function PickSplit({ pickSplit }: { pickSplit: PickSplitRow }) {
  const columnCount = pickSplit.pickOutcomes.length;

  return (
    <Grid
      className="overflow-hidden rounded-b-md"
      style={{ gridTemplateColumns: `repeat(${columnCount}, 1fr)` }}
    >
      {pickSplit.pickOutcomes.map((pickOutcome, i) => {
        return (
          <div className="relative" key={i}>
            <TeamSegment
              team={pickOutcome.team}
              outcomeChance={pickOutcome.chance ?? 0}
            />
            {i != columnCount - 1 && (
              <div
                className="absolute right-[-0.75rem] top-1/2 z-10 -translate-y-1/2 transform rounded-md
                  bg-white"
              >
                <Redo color="#4B5563" />
              </div>
            )}
          </div>
        );
      })}
    </Grid>
  );
}

export function TeamSegment({
  team,
  outcomeChance,
}: {
  team: TeamRow;
  outcomeChance: number;
}) {
  const teamAttribtues = teams.find(
    (teamAttributes) => teamAttributes.TEAM_ID == team.nbaId
  );
  const teamColor = teamAttribtues?.TEAM_COLOR;
  const opacity = outcomeChance / 100 + 0.5;

  return (
    <Flex
      justify="center"
      align="center"
      className="h-8 text-white"
      style={{
        backgroundColor: teamColor,
        opacity,
      }}
    >
      <Flex gap="xs">
        <Text heading>{team.code}</Text>
        <Text>{outcomeChance + "%"}</Text>
      </Flex>
    </Flex>
  );
}

export function PickProbabilityItem({
  pickOutcomes,
  pickNumber,
}: {
  pickOutcomes: PickOutcomeRow[];
  pickNumber: number;
}) {
  return (
    <TooltipProvider>
      <Tooltip delayDuration={0}>
        <TooltipTrigger className="relative">
          {pickOutcomes.map((pickOutcome, i) => (
            <PickProbabilityBar
              key={i}
              pickOutcome={pickOutcome}
              pickNumber={pickNumber}
            />
          ))}
        </TooltipTrigger>
        <TooltipContent>
          <Text className="mb-2">{formatRank(pickNumber) + " Pick"}</Text>
          <Flex gap="xs" direction="right" className="opacity-70">
            <Text heading>
              {pickOutcomes.reduce((acc, pickOutcome) => {
                const key = `pick${pickNumber}Chance` as keyof PickOutcomeRow;
                const probability = pickOutcome[key] as number | null;
                return acc + (probability ?? 0);
              }, 0)}
              %
            </Text>
            <Text>chance</Text>
          </Flex>
          {pickOutcomes.map((pickOutcome, i) => (
            <PickProbabilityTooltip
              key={i}
              pickOutcome={pickOutcome}
              pickNumber={pickNumber}
            />
          ))}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function PickProbabilityBar({
  pickOutcome,
  pickNumber,
}: {
  pickOutcome: PickOutcomeRow;
  pickNumber: number;
}) {
  const key = `pick${pickNumber}Chance` as keyof PickOutcomeRow;
  const probability = pickOutcome[key] as number | null;
  if (!probability) return null;

  const teamAttributes = teams.find(
    (teamAttributes) => teamAttributes.TEAM_ID == pickOutcome.team?.nbaId
  );
  const teamColor = teamAttributes?.TEAM_COLOR;

  return (
    <>
      <div
        className="absolute bottom-[2px] left-1 w-4 max-sm:left-[7px]"
        style={{
          height: `${probability / 3}px`,
          backgroundColor: teamColor,
        }}
      />
      <div className="absolute bottom-[2px] h-10 w-10" />
    </>
  );
}

export function PickProbabilityTooltip({
  pickOutcome,
  pickNumber,
}: {
  pickOutcome: PickOutcomeRow;
  pickNumber: number;
}) {
  const key = `pick${pickNumber}Chance` as keyof PickOutcomeRow;
  const probability = pickOutcome[key] as number | null;

  const teamAttributes = teams.find(
    (teamAttributes) => teamAttributes.TEAM_ID == pickOutcome.team?.nbaId
  );
  const teamColor = teamAttributes?.TEAM_COLOR;

  return (
    <Flex gap="xs">
      <CornerDownRight className="opacity-70" />
      <Text className="opacity-70">{probability + "% to "}</Text>
      <Text heading style={{ color: teamColor }}>
        {pickOutcome.team.code}
      </Text>
    </Flex>
  );
}

export function PickTick() {
  return (
    <div className="relative">
      <div className="absolute bottom-[2px] left-6 h-4 w-[2px] bg-gray-500" />
    </div>
  );
}
